import { useState } from "react";
import { CompanyData, StockHolding } from "@/contexts/GameContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useGame } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";

interface Props {
  company: CompanyData;
  holding?: StockHolding;
}

type OrderType = "market" | "limit";
type Side = "buy" | "sell";

export default function TradePanel({ company, holding }: Props) {
  const { isDark } = useTheme();
  const { placeStockOrder } = useGame();
  const { user, spendCoins, addCoins, buyStock, sellStock } = useAuth();

  const [side, setSide] = useState<Side>("buy");
  const [orderType, setOrderType] = useState<OrderType>("market");
  const [shares, setShares] = useState<string>("100");
  const [limitPrice, setLimitPrice] = useState<string>(company.stockPrice.toString());
  const [message, setMessage] = useState<{ text: string; ok: boolean } | null>(null);
  const [loading, setLoading] = useState(false);

  const cardBg = isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";
  const inputBg = isDark ? "bg-white/5 border-white/10 text-white placeholder-gray-500" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400";
  const divider = isDark ? "border-white/5" : "border-gray-100";

  const sharesNum = parseInt(shares) || 0;
  const execPrice = orderType === "market" ? company.stockPrice : (parseFloat(limitPrice) || company.stockPrice);
  const totalCost = sharesNum * execPrice;
  const canAfford = (user?.coins ?? 0) >= totalCost;
  const canSell = (holding?.shares ?? 0) >= sharesNum;

  const handleSubmit = async () => {
    if (!user) return;
    if (sharesNum <= 0) {
      setMessage({ text: "株数を入力してください", ok: false });
      return;
    }

    setLoading(true);
    setMessage(null);

    const result = placeStockOrder(
      {
        companyId: company.id,
        type: side,
        orderType,
        shares: sharesNum,
        limitPrice: orderType === "limit" ? parseFloat(limitPrice) : undefined,
        placedBy: user.id,
        placedByName: user.username,
      },
      spendCoins,
      addCoins,
      user.stockPortfolio
    );

    if (result.success) {
      if (side === "buy") {
        buyStock(company.id, sharesNum, execPrice);
      } else {
        sellStock(company.id, sharesNum);
      }
    }

    setMessage({ text: result.message, ok: result.success });
    setLoading(false);
  };

  const setSharesPreset = (pct: number) => {
    if (side === "buy") {
      const maxShares = Math.floor((user?.coins ?? 0) / execPrice);
      setShares(Math.floor(maxShares * pct).toString());
    } else {
      setShares(Math.floor((holding?.shares ?? 0) * pct).toString());
    }
  };

  return (
    <div className={`rounded-2xl border ${cardBg}`}>
      {/* Side Toggle */}
      <div className="p-4 pb-0">
        <div className={`flex rounded-xl p-1 ${isDark ? "bg-white/5" : "bg-gray-100"}`}>
          <button
            onClick={() => setSide("buy")}
            className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
              side === "buy"
                ? "bg-emerald-500 text-white shadow-lg"
                : isDark ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"
            }`}
          >
            買い注文
          </button>
          <button
            onClick={() => setSide("sell")}
            className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
              side === "sell"
                ? "bg-red-500 text-white shadow-lg"
                : isDark ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"
            }`}
          >
            売り注文
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Order Type */}
        <div>
          <label className={`text-xs font-semibold mb-2 block ${textSecondary}`}>注文種別</label>
          <div className="flex gap-2">
            {(["market", "limit"] as OrderType[]).map((t) => (
              <button
                key={t}
                onClick={() => setOrderType(t)}
                className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap border ${
                  orderType === t
                    ? isDark ? "border-amber-400/50 bg-amber-400/10 text-amber-400" : "border-amber-500/50 bg-amber-50 text-amber-600"
                    : isDark ? "border-white/10 text-gray-400 hover:border-white/20" : "border-gray-200 text-gray-500 hover:border-gray-300"
                }`}
              >
                {t === "market" ? "成行" : "指値"}
              </button>
            ))}
          </div>
        </div>

        {/* Limit Price (指値のみ) */}
        {orderType === "limit" && (
          <div>
            <label className={`text-xs font-semibold mb-2 block ${textSecondary}`}>指値価格 (coins/株)</label>
            <input
              type="number"
              value={limitPrice}
              onChange={(e) => setLimitPrice(e.target.value)}
              className={`w-full rounded-xl border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/30 ${inputBg}`}
              min="1"
              step="1"
            />
          </div>
        )}

        {/* Shares */}
        <div>
          <label className={`text-xs font-semibold mb-2 block ${textSecondary}`}>株数</label>
          <input
            type="number"
            value={shares}
            onChange={(e) => setShares(e.target.value)}
            className={`w-full rounded-xl border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/30 ${inputBg}`}
            min="1"
            step="1"
          />
          {/* Preset buttons */}
          <div className="flex gap-1.5 mt-2">
            {[0.25, 0.5, 0.75, 1].map((pct) => (
              <button
                key={pct}
                onClick={() => setSharesPreset(pct)}
                className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                  isDark ? "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-900"
                }`}
              >
                {pct === 1 ? "全部" : `${pct * 100}%`}
              </button>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div className={`rounded-xl p-3 space-y-2 ${isDark ? "bg-white/3" : "bg-gray-50"}`}>
          <div className="flex justify-between text-xs">
            <span className={textSecondary}>執行価格</span>
            <span className={`font-bold ${textPrimary}`}>{execPrice.toLocaleString()} coins/株</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className={textSecondary}>株数</span>
            <span className={`font-bold ${textPrimary}`}>{sharesNum.toLocaleString()} 株</span>
          </div>
          <div className={`border-t pt-2 ${divider}`}>
            <div className="flex justify-between text-sm">
              <span className={`font-semibold ${textSecondary}`}>{side === "buy" ? "合計コスト" : "受取コイン"}</span>
              <span className={`font-black ${side === "buy" ? (canAfford ? "text-emerald-400" : "text-red-400") : "text-emerald-400"}`}>
                {totalCost.toLocaleString()} coins
              </span>
            </div>
          </div>
        </div>

        {/* Balance / Holdings Info */}
        <div className="flex justify-between text-xs">
          <span className={textSecondary}>
            {side === "buy" ? "保有コイン" : "保有株数"}
          </span>
          <span className={`font-bold ${
            side === "buy"
              ? canAfford ? "text-emerald-400" : "text-red-400"
              : canSell ? "text-emerald-400" : "text-red-400"
          }`}>
            {side === "buy"
              ? `${(user?.coins ?? 0).toLocaleString()} coins`
              : `${(holding?.shares ?? 0).toLocaleString()} 株`
            }
          </span>
        </div>

        {/* Error/Success Message */}
        {message && (
          <div className={`rounded-xl px-4 py-3 text-sm font-medium ${
            message.ok
              ? "bg-emerald-400/10 text-emerald-400"
              : "bg-red-400/10 text-red-400"
          }`}>
            <i className={`${message.ok ? "ri-check-line" : "ri-error-warning-line"} mr-2`}></i>
            {message.text}
          </div>
        )}

        {/* Submit Button */}
        {!user ? (
          <div className={`text-center text-sm py-3 ${textSecondary}`}>
            ログインして取引を開始
          </div>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={loading || (side === "buy" ? !canAfford : !canSell) || sharesNum <= 0}
            className={`w-full py-3 rounded-xl text-sm font-black transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${
              side === "buy"
                ? "bg-emerald-500 hover:bg-emerald-400 text-white"
                : "bg-red-500 hover:bg-red-400 text-white"
            }`}
          >
            {loading ? "処理中..." : side === "buy" ? `${sharesNum.toLocaleString()}株 購入する` : `${sharesNum.toLocaleString()}株 売却する`}
          </button>
        )}
      </div>
    </div>
  );
}
